import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ApplicationService} from '@service/services';
import {SocketService} from '@service/services/server/socket.service';
import {Router} from '@angular/router';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {environment} from '@env/environment';
import {SharedkeyDataService} from '@service/services/common-services/sharedkey-data.service';
import {ApplicationModel, KeyIdentifierModel} from '@service/models';
import {ApplicantType} from '@service/enum/process-state.enum';


@Injectable({
    providedIn: 'root'
})
export class ProvisionService {
    apiTimeout: number = 5 * 1000;
    apiInterval: number = 5 * 1000;
    maxTimeout: number = 60 * 1000;
    delayTimeout: number = 15 * 60 * 1000;
    currentTimeout: number = 0;
    currentFeatureData: any;
    checkStatusCall: any;
    currentProvision: BehaviorSubject<any> = new BehaviorSubject(null);
    mapFeatureWithStatus: any = {
        payment: 'PAYMENT_SUCCESS',
        esign: 'DOCS_SIGNED',
        kyc: 'VERIFICATION_SUCCESS',
        scheduling: 'VERIFICATION_SUCCESS'
    };
    private keyIdentifier: KeyIdentifierModel;

    constructor(private http: HttpClient,
                private applicationService: ApplicationService,
                private socketService: SocketService,
                private sharedKeyDataService: SharedkeyDataService,
                private router: Router) {
        this.sharedKeyDataService.keyIdentifier.subscribe(data => {
            this.keyIdentifier = data;
        });
        this.currentProvision.subscribe(data => {
            console.log('result12312', data);
            this.currentFeatureData = data;
        });
    }


    /**
     * Get next provision for application
     * @param _applicationId
     */
    getNextProvision(_applicationId: number) {
        return new Promise((resolve, reject) => {

            let apiEndPoint = `/provision/application/${_applicationId}`;
            if (this.keyIdentifier.isCoBoConsentFlow) {
                apiEndPoint = `/provision/coapplicant/application/${_applicationId}`;
            }

            this.http.get<any>(environment.apiUrl + apiEndPoint).subscribe({
                next: async (result) => {
                    const {feature, statusVerifyMethod: verifyMethod} = result;


                    if (feature.toLowerCase().includes('thankyou')) {
                        resolve(result);
                        this.currentProvision.next({name: feature.toLowerCase(), verifyMethod});
                        this.router.navigate([`/provision/thankyou`], {
                            state: {messageName: feature, isSkipAllowed: result.isSkipAllowed}
                        });
                    } else if (feature === 'POST-COAPPLICANT-TO-CORE') {
                        const application: ApplicationModel = await firstValueFrom(this.applicationService.findApplication(this.keyIdentifier.applicationId));
                        const secondaryApplicant = application.applicants.find(item => item.applicantType === ApplicantType.SECONDARY);

                        if (secondaryApplicant && secondaryApplicant.addedToCore === true) {
                            this.router.navigate([`/provision/thankyou`]);
                        } else {
                            this.postToCoreCoBo(secondaryApplicant.applicationId).subscribe({
                                next: (resp: any) => {
                                    if (resp) {
                                        this.keyIdentifier.errorCode = application.applicationStatus;
                                        this.sharedKeyDataService.announceIdentifierChanges(this.keyIdentifier);
                                    }
                                    this.router.navigate([`/provision/thankyou`]);
                                }, error: () => {
                                    // if error in post to core we have to get application status and redirect to thankyou page
                                    this.router.navigate([`/provision/thankyou`]);
                                }
                            });
                        }

                        resolve(result);
                    } else {
                        resolve(result);
                        this.currentProvision.next({name: feature.toLowerCase(), verifyMethod});
                        this.router.navigate([`/provision/${feature.toLowerCase()}`], {state: {verifyMethod: (verifyMethod || '').toUpperCase()}});
                    }
                }, error: error => {
                    resolve(error);
                    this.router.navigate([`/provision/thankyou`]);
                }
            });
        });
    }

    skipToNextProvision(_applicationId: number) {
        this.http.post<any>(environment.apiUrl + `/provision/skip/${this.currentFeatureData.name}/application/${_applicationId}`, {}).subscribe(res => {
            this.router.navigate(['/redirect'], {queryParams: {appId: _applicationId, event: 'success', feature: this.currentFeatureData.name, coBorrowerFlow: (this.keyIdentifier.isCoBoConsentFlow) ? 'Y' : ''}});
        });
    }

    verifyAndRedirectProvisions(verificationMethod: 'POOLING' | 'POLLING' | 'REALTIME' | 'SOCKET' | 'NONE' | string, applicationId: number, socketUrl: string = '', onNavigate?: () => void, onDDFailed?: () => void) {
        console.log('verificationMethod >>>>>>>>>>>', verificationMethod);
        switch (verificationMethod) {
            case 'POOLING':
            case 'POLLING':
                // KYC, INCOME VERIFICATION example (will slowly move to socket)
                this.verifyByPolling(applicationId);
                break;
            case 'REALTIME':
                // PRE-REGISTRATION, UPLOAD_DOCS, PAYMENT=ACH/internalTransfer example
                this.verifyByRealTime(applicationId);
                break;
            case 'SOCKET':
                // PAYMENT=CARD example
                this.verifyBySocket(applicationId, socketUrl, onNavigate, onDDFailed);
                break;
            case 'NONE':
                console.log('UPDATE FROM FRONTEND');
                break;
            default:
                this.verifyByRealTime(applicationId);
        }
    }


    verifyByPolling(applicationId: number) {
        if (this.currentTimeout >= this.maxTimeout) {
            return;
        }
        this.currentTimeout = this.currentTimeout + this.apiTimeout;
        setTimeout(() => {
            this.checkStatusCall = setInterval(() => this.getApplicationStatus(applicationId), this.apiInterval);
        }, this.delayTimeout);
    }


    async verifyByRealTime(applicationId: number) {
        // this.router.navigate(['/provision'], { relativeTo: this.activatedRoute });
        await this.getNextProvision(applicationId);
        // this.router.navigate(['/redirect'], {queryParams: {appId: applicationId, event: 'success', feature: this.currentFeatureData.name}});
    }


    verifyBySocket(applicationId: number, sessionId: string, onNavigate?: () => void, onDDFailed?: () => void) {
        // start checking application status after 60 seconds with interval of 5 seconds in case socket doesn't send the event.
        setTimeout(() => {
            this.checkStatusCall = setInterval(() => this.getApplicationStatus(applicationId), this.apiInterval);
        }, this.delayTimeout);
        this.socketService.connectAsync(sessionId).then(res => {
            if (res) {
                this.socketService.socketDataSource.subscribe(socketRes => {
                    if (socketRes && (socketRes.action === 'CHECKPAYMENTSTATUS' ||
                        socketRes.action === 'CHECKIDVERIFICATIONSTATUS' ||
                        socketRes.action === 'CHECKDDSETUPSTATUS')) {
                        if (this.checkStatusCall) { clearInterval(this.checkStatusCall); }
                        onNavigate?.();
                        this.router.navigate(['/redirect'], {queryParams: {appId: applicationId, event: 'success', feature: this.currentFeatureData.name, coBorrowerFlow: (this.keyIdentifier.isCoBoConsentFlow) ? 'Y' : ''}});
                    }
                    if (socketRes && socketRes.action === 'CHECKDDSETUPSTATUS_FAILED') {
                        if (this.checkStatusCall) { clearInterval(this.checkStatusCall); }
                        if (onDDFailed) {
                            onDDFailed();
                        } else {
                            onNavigate?.();
                            this.router.navigate(['/redirect'], {queryParams: {appId: applicationId, event: 'success', feature: this.currentFeatureData.name, coBorrowerFlow: (this.keyIdentifier.isCoBoConsentFlow) ? 'Y' : ''}});
                        }
                    }
                });
            }
        });
    }

    getApplicationStatus(applicationId: number) {

        this.applicationService.findApplication(applicationId)
            .subscribe(async res => {
                if (this.mapFeatureWithStatus[this.currentFeatureData.name] && this.mapFeatureWithStatus[this.currentFeatureData.name] === res.losStatus) {
                    clearInterval(this.checkStatusCall);
                    this.router.navigate(['/redirect'], {queryParams: {appId: applicationId, event: 'success', feature: this.currentFeatureData.name, coBorrowerFlow: (this.keyIdentifier.isCoBoConsentFlow) ? 'Y' : ''}});
                } else {

                }
            }, error1 => {
                clearInterval(this.checkStatusCall);
                this.router.navigate(['/myapp']);
            });
    }

    generateESignUrl(applicationId) {
        const url = `${environment.apiUrl}/docusign/embeddedurl`;
        const body = {
            returnUrl: `${window.location.protocol}//${window.location.host}/redirect?appId=${applicationId}&feature=esign`,
            applicationId: Number(applicationId),
            institutionCode: environment.institutionId
        };

        if (this.keyIdentifier.isCoBoConsentFlow) {
            body.returnUrl += '&coBorrowerFlow=Y';
        }
        return this.http.post<any>(url, body);
    }

    sendIdPalLink(applicationId, appendHeader = false) {
        const url = `${environment.apiUrl}/idpal/app-link/send/${applicationId}`;
        return this.http.post<any>(url, {}, {
            ...(appendHeader ? {
                headers: {'Communication-Type': 'TEXT'}
            } : {})
        });
    }

    checkIdPalStatus(applicationId) {
        const url = `${environment.apiUrl}/idpal/app-link/status/${applicationId}`;
        return this.http.get<any>(url);
    }

    scheduleAppointment(appId) {
        const url = `${environment.apiUrl}/appointment/${appId}`;
        return this.http.post<any>(url, {});
    }

    /**
     * Post To Core CoApplicant
     */
    postToCoreCoBo(applicationId) {
        return this.http.put<any>(`${environment.apiUrl}/deposit/account/applications/${applicationId}/core/addjointowner`, {});
    }

    goToUrlTarget(urlTarget): any {
        let navigationAction: string = '';
        let screenAction: string = '';
        let errorCode: string = '';
        switch (urlTarget) {
            case 'docsUpload':
                this.currentProvision.next({name: 'upload-docs'});
                navigationAction = '/provision/upload-docs';
                screenAction = 'provision/upload-docs';
                errorCode = '';
                break;
        }
        return {navigationAction, screenAction, errorCode};
    }


}